/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Input, OnChanges, SimpleChanges, signal, WritableSignal} from '@angular/core';
import template from './line-item-creative-list.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {CreativeUserStatesComponent} from '../common/directives/creative-user-states';

import {asUrlGroupList, asUrlList} from '../creative/creative-url-utils';
import {advertiserNavigationParams} from '../../components/util/navigation-context';

const EMPTY_LIST: any[] = [];
const EMPTY_RECORD: Record<string, any> = {};

/** Embedded creative table on line item / creative set edit — not a standalone list page. */
@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, CreativeUserStatesComponent],
  selector: 'adn-line-item-creative-list',
  template
})
export class LineItemCreativeListComponent implements OnChanges, DoCheck {
  @Input() resource: any;
  @Input() readOnly: boolean;

  showCreativeSizes = false;

  private readonly listItemsState = signal<any[]>(EMPTY_LIST);
  private readonly listResolvedState = signal(false);
  private readonly showPaginationState = signal(false);
  private readonly availableFixedState = signal<any[]>(EMPTY_LIST);
  private readonly availableRangesState = signal<any[]>(EMPTY_LIST);
  private readonly hasDeliverableSizesState = signal(false);
  private readonly unmatchedCreativesState = signal<Record<string, any>>(EMPTY_RECORD);

  get listItems(): any[] { return this.listItemsState(); }
  get listTotalCount(): number { return this.resource?.listState?.totalCount() || 0; }
  get listPageSize(): number { return this.resource?.listState?.pageSize || 25; }
  get listResolved(): boolean { return this.listResolvedState(); }
  get isNewResource(): boolean { return !!this.resource?.isNew; }
  get hasItems(): boolean { return this.listItems.length > 0; }
  get showPagination(): boolean { return this.showPaginationState(); }
  get availableFixed(): any[] { return this.availableFixedState(); }
  get availableRanges(): any[] { return this.availableRangesState(); }
  get hasDeliverableSizes(): boolean { return this.hasDeliverableSizesState(); }
  get unmatchedCreatives(): Record<string, any> { return this.unmatchedCreativesState(); }

  readonly sortHeaderParams = [
    {param: 'name', label: 'creative'},
    {label: 'info', param: '', sortable: false},
    {param: '', label: 'targeting', sortable: false}
  ];

  readonly itemStateName = 'app.adv.creatives.creative';
  readonly newItemStateName = 'app.adv.creatives.new';
  readonly bulkUploadStateName = 'app.adv.creatives.bulkupload';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly externalDemandSourceItemStateName = 'app.adm.external-demand-sources.external-demand-source';

  ngOnChanges(_changes: SimpleChanges): void {
    this.syncViewState();
  }

  ngDoCheck(): void {
    this.syncViewState();
  }

  hideBulkSelect(): boolean {
    return !!this.resource?.readOnly || !!this.resource?.isAxPublisher;
  }

  creativeLinkParams(creative: any): any {
    return {
      fromLineItem: !this.resource?.isCreativeSet,
      fromCreativeSet: !!this.resource?.isCreativeSet,
      id: creative.id
    };
  }

  newCreativeParams(): any {
    const model = this.resource?.model || {};
    return {
      fromCreativeSet: !!this.resource?.isCreativeSet,
      fromLineItem: !this.resource?.isCreativeSet,
      modelName: model.name,
      modelId: model.id,
      advertiserName: model.advertiser?.name,
      advertiserId: model.advertiser?.id
    };
  }

  bulkUploadParams(): any {
    const model = this.resource?.model || {};
    const advertiserParams = model.advertiser ? advertiserNavigationParams(model.advertiser) : {};
    const parentParams = this.resource?.isCreativeSet
      ? {creativeSetId: model.id, creativeSetName: model.name}
      : {lineItemId: model.id, lineItemName: model.name};
    return {...advertiserParams, ...parentParams};
  }

  isUnmatchedCreative(creative: any): boolean {
    return !!this.unmatchedCreatives?.[creative.id];
  }

  undeliverableMessage(creative: any): string {
    return `The targeted inventory does not support the delivery of ${creative.width}×${creative.height} creatives`;
  }

  layoutLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_LAYOUTS !== true;
  }

  externalDemandLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_SYSTEM !== true;
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }

  private syncViewState(): void {
    const listState = this.resource?.listState;
    const items = listState?.items?.() || EMPTY_LIST;
    const totalCount = listState?.totalCount?.() || 0;
    const pageSize = listState?.pageSize || 25;
    const availableFixed = this.resource?.availableFixed || EMPTY_LIST;
    const availableRanges = this.resource?.availableRanges || EMPTY_LIST;
    this.setIfChanged(this.listItemsState, items);
    this.setIfChanged(this.listResolvedState, !!listState && !listState.loading?.());
    this.setIfChanged(this.showPaginationState, totalCount > pageSize || totalCount > 5);
    this.setIfChanged(this.availableFixedState, availableFixed);
    this.setIfChanged(this.availableRangesState, availableRanges);
    this.setIfChanged(this.hasDeliverableSizesState, !!this.resource?.checksDone
      && !!(availableFixed.length || availableRanges.length));
    this.setIfChanged(this.unmatchedCreativesState, this.resource?.unmatchedCreatives || EMPTY_RECORD);
  }

  private setIfChanged<T>(state: WritableSignal<T>, value: T): void {
    if (!Object.is(state(), value)) {
      state.set(value);
    }
  }
}
